import { Box, Drawer, useMediaQuery, BottomSheet, Form } from "@ui-kit/components/index.es";
import { bool, func, arrayOf, shape } from "prop-types";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { actions as fundcorpActions, selectors as fundcorpSelectors } from "reducers/fundcorp";
import Yup from "yup";
import Notification from "pages/_components/Notification";

const FORM_ID = "fund.operation.fundExtracts";

function MonthlyReportsDrawer(props) {
    MonthlyReportsDrawer.propTypes = {
        dispatch: func.isRequired,
        isReportDrawerVisible: bool.isRequired,
        formikProps: shape({}).isRequired,
        shareAccounts: arrayOf(shape({})).isRequired,
        fundFeatures: arrayOf(shape({})).isRequired,
        funds: arrayOf(shape({})).isRequired,
        fechingReport: bool.isRequired,
        embeddedInV3Shell: bool,
    };

    MonthlyReportsDrawer.defaultProps = {
        embeddedInV3Shell: false,
    };

    const {
        dispatch,
        isReportDrawerVisible,
        formikProps,
        shareAccounts,
        funds,
        fundFeatures,
        fechingReport,
        embeddedInV3Shell,
    } = props;

    // Inside the v3 private shell with the dashboard UX flag on, the toast in `PrivateLayout`
    // (`v3/components/ToastNotification.jsx`) is the single source of feedback for the download
    // result. Hide the legacy `Notification` here to avoid showing both at once.
    const showLegacyNotification = !(embeddedInV3Shell && isUxDashboardImprovementsEnabled());

    const limitPeriod = configUtils.getInteger("frontend.fundExtracts.searchPeriod", 12);
    let limitPeriodTotal = configUtils.getInteger("backend.fundExtracts.searchPeriodTotal", 24);
    limitPeriodTotal = limitPeriodTotal > limitPeriod ? limitPeriodTotal : limitPeriod;
    const initDate = new Date();
    initDate.setMonth(initDate.getMonth() - 1);

    const [shareAccountOptions, setShareAccountOptions] = useState([]);
    const [fundOptions, setFundOptions] = useState([]);
    const [dateFrom, setDateFrom] = useState(new Date(initDate.getFullYear(), initDate.getMonth(), 1));
    const [dateTo, setDateTo] = useState(new Date(initDate.getFullYear(), initDate.getMonth() + 1, 0));
    const [submitInactive, setSubmitInactive] = useState(false);
    const [reportDrawerValues, setReportDrawerValues] = useState({
        shareAccount: "",
        selectedFunds: false,
        funds: [],
        dates: [dateFrom, dateTo],
    });

    useEffect(() => {
        if (formikProps?.setValues) {
            formikProps.setValues({ dates: [dateFrom, dateTo] });
        }
        // eslint-disable-next-line
    }, [formikProps]);

    useEffect(() => {
        setShareAccountOptions(
            shareAccounts?.map(({ id, description }) => ({
                value: id,
                label: `${id} ${description}`,
            })),
        );
        let fundsMap;
        if (funds?.length > 0) {
            fundsMap = funds?.map(({ id, description }) => ({
                value: id,
                label: description,
            }));
        } else {
            fundsMap = fundFeatures?.map(({ numero, nombre }) => ({
                value: numero,
                label: nombre,
            }));
        }
        // eslint-disable-next-line no-unused-expressions
        fundsMap && setFundOptions(fundsMap);
    }, [shareAccounts, funds, fundFeatures]);

    useEffect(() => {
        const datesFilter = reportDrawerValues?.dates.filter((d) => d != null);
        const issetFund = reportDrawerValues?.selectedFunds && reportDrawerValues?.funds?.length <= 0;
        setSubmitInactive(issetFund || datesFilter?.length <= 0);
    }, [reportDrawerValues]);

    const isMobile = useMediaQuery("min", "md");

    const getMinDate = () => {
        const configDate = new Date();
        configDate.setMonth(configDate.getMonth() - limitPeriodTotal);
        return configDate;
    };

    // eslint-disable-next-line no-shadow
    const handleOnSubmit = ({ format, shareAccount, funds, ...values }, { setSubmitting }) => {
        if (fechingReport) {
            setSubmitting(false);
            return;
        }
        const fundMap = funds?.map(({ value }) => value);
        const selectedAccountId = shareAccount?.value ?? shareAccount;
        const selectedAccount = shareAccounts.find((sa) => sa.id === selectedAccountId);
        dispatch(
            fundcorpActions.downloadMonthlyReport(
                { ...values, shareAccount: selectedAccountId, funds: fundMap, verifyCode: selectedAccount?.verifyCode },
                format || "pdf",
            ),
        );
        // Fire-and-forget (Redux saga): clear Formik submitting so Form's
        // `loading || isSubmitting` follows the controlled `loading` prop.
        setSubmitting(false);
    };

    const inputList = [
        {
            colProps: {
                size: 12,
            },
            component: "selectField",
            id: "shareAccount",
            required: true,
            placeholder: i18n.get(`${FORM_ID}.shareaccount.placeholder`),
            selectLabel: i18n.get(`${FORM_ID}.shareaccount.label`),
            defaultOption:
                shareAccountOptions?.length > 0
                    ? shareAccounts.find((sAccount) => sAccount.isFavorite)?.id || shareAccountOptions[0]?.value
                    : null,
            options: shareAccountOptions,
            value: reportDrawerValues?.shareAccount,
            onSelectOption: (option) => setReportDrawerValues({ ...reportDrawerValues, shareAccount: option?.value }),
            searchable: !isMobile,
            iconMobile: "TrendUp",
        },
        {
            colProps: {
                size: 12,
            },
            component: "switch",
            name: "selectedFunds",
            id: "selectedFunds",
            label: i18n.get(`${FORM_ID}.funds.switch.label`),
            tooltipText: i18n.get(`${FORM_ID}.funds.switch.tooltipText`),
            tooltipPosition: "bottom",
            isChecked: reportDrawerValues?.selectedFunds,
            onChange: (e) => {
                setReportDrawerValues({ ...reportDrawerValues, selectedFunds: e.target?.checked });
            },
        },
    ];

    // eslint-disable-next-line no-unused-expressions
    reportDrawerValues?.selectedFunds &&
        inputList.push({
            colProps: {
                size: 12,
            },
            component: "selectField",
            id: "funds",
            placeholder: i18n.get(`${FORM_ID}.funds.placeholder`),
            selectLabel: i18n.get(`${FORM_ID}.funds.label`),
            isMulti: true,
            maxLength: 50,
            required: true,
            onSelectOption: (options) =>
                setReportDrawerValues({ ...reportDrawerValues, funds: options.map(({ value }) => value) }),
            options: fundOptions,
            value: [],
            iconMobile: "TrendUp",
        });

    inputList.push({
        colProps: {
            size: 12,
        },
        component: "rangeDateField",
        id: "dates",
        label: i18n.get(`${FORM_ID}.dates.label`),
        required: true,
        minDate: getMinDate(),
        maxDate: moment()
            ?.subtract(1, "days")
            .toDate(),
        value: [dateFrom, dateTo],
        maxMonthsOfRange: limitPeriod,
        onDateChange: ([from, to]) => {
            setReportDrawerValues({ ...reportDrawerValues, dates: [from, to] });
            setDateFrom(from);
            setDateTo(to);
        },
    });

    const submitBtn = {
        colProps: {
            size: {
                lg: 12,
                xs: 12,
            },
        },
        text: i18n.get(`global.download`),
        variant: "solid",
        icon: "SendDown",
        iconVariant: "outline",
        isInactive: submitInactive || fechingReport,
        loading: fechingReport,
    };

    const validations = Yup.lazy((values) =>
        Yup.object().shape({
            shareAccount: Yup.string().required(i18n.get(`${FORM_ID}.field.error.required`)),
            funds: !values?.selectedFunds
                ? Yup.array().of(Yup.object().shape({}))
                : Yup.array()
                      .of(Yup.object().shape({}))
                      .min(1, i18n.get(`${FORM_ID}.field.error.required`)),
            dates: Yup.mixed().test("dates", i18n.get(`${FORM_ID}.field.error.required`), ([from, to]) => from && to),
        }),
    );

    const getSubmitOptions = () => {
        const formats = [
            i18n.get("global.download.pdf"),
            i18n.get("global.download.xls"),
            i18n.get("global.download.csv"),
        ];
        return formats.map((format) => ({ name: "format", value: format, label: format }));
    };

    return (
        <Box flex>
            {isMobile ? (
                <BottomSheet
                    iconTitle="Memo"
                    iconColor="primary-pressed-color"
                    title={i18n.get(`${FORM_ID}.title`)}
                    className="px-1 text-left"
                    isVisible={isReportDrawerVisible}
                    onClose={() => {
                        dispatch(fundcorpActions.setReportsDrawerVisible(false));
                    }}>
                    {showLegacyNotification && (
                        <Notification notificationClassname="snackbar__extract" scopeToShow="monthlyReportDrawer" />
                    )}
                    <Form
                        className="pb-8 pt-3"
                        disabledSubmitByDirty
                        hiddeForm={false}
                        rowSpacingForm={32}
                        buttonSubmit={submitBtn}
                        initialValues={reportDrawerValues}
                        inputsList={inputList}
                        onSubmit={handleOnSubmit}
                        validationSchema={validations}
                    />
                </BottomSheet>
            ) : (
                <Drawer
                    controlElementId="open-menu"
                    keepFixedOnWeb={false}
                    iconTitle="Memo"
                    iconColor="primary-pressed-color"
                    mainContentId="drawer-left-main"
                    onClose={() => dispatch(fundcorpActions.setReportsDrawerVisible(false))}
                    placement="right"
                    isVisible={isReportDrawerVisible}
                    w={400}
                    title={i18n.get(`${FORM_ID}.title`)}>
                    {showLegacyNotification && (
                        <Notification notificationClassname="snackbar__extract" scopeToShow="monthlyReportDrawer" />
                    )}
                    <Box mt={24}>
                        <Form
                            disabledSubmitByDirty={false}
                            hiddeForm={false}
                            rowSpacingForm={32}
                            wrapButtons
                            buttonSubmit={{
                                colProps: {
                                    size: {
                                        lg: 12,
                                        xs: 12,
                                    },
                                },
                                text: i18n.get(`global.download`),
                                variant: "solid",
                                icon: "SendDown",
                                iconVariant: "outline",
                                isInactive: submitInactive || fechingReport,
                                loading: fechingReport,
                            }}
                            initialValues={{
                                ...reportDrawerValues,
                                dates: [dateFrom, dateTo],
                            }}
                            inputsList={inputList}
                            buttonSubmitSubOptions={getSubmitOptions()}
                            onSubmit={handleOnSubmit}
                            validationSchema={validations}
                        />
                    </Box>
                </Drawer>
            )}
        </Box>
    );
}

const mapStateToProps = (state) => ({
    isReportDrawerVisible: fundcorpSelectors.isReportDrawerVisible(state),
    shareAccounts: fundcorpSelectors.getShareAccounts(state),
    funds: fundcorpSelectors.getFunds(state),
    fundFeatures: fundcorpSelectors.getFundFeatures(state),
    fechingReport: fundcorpSelectors.getFetchingMonthlyReport(state),
});
export default connect(mapStateToProps)(MonthlyReportsDrawer);
